"use client";

import { SEARCH_IRRELEVANCE_CAROUSEL_COMPONENT_NAME } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/consts";
import { ComponentLoggingEventParams } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/types";
import { TrackedLinkContext } from "@faire/deprecated-retailer-visitor-shared/components/TrackedLink/index";
import { ProductTileClickImpressionProvider } from "@faire/deprecated-retailer-visitor-shared/hooks/ProductTileImpression/ProductTileClickImpressionProvider";
import { ProductTileViewImpressionProvider } from "@faire/deprecated-retailer-visitor-shared/hooks/ProductTileImpression/ProductTileViewImpressionProvider";
import { CATEGORY_PRODUCT_TRACK_CLICK_CONTAINER_NAME } from "@faire/deprecated-retailer-visitor-shared/services/consts";
import { Surface } from "@faire/web-api--source/com/faire/logging/Surface";
import { DisplayPageType } from "@faire/web-api--source/indigofair/data/DisplayPageType";
import { IClickInfo } from "@faire/web-api--source/indigofair/data/IClickInfo";
import { ILayoutElement } from "@faire/web-api--source/indigofair/data/ILayoutElement";
import { ProductTileContainer } from "@faire/web-api--source/indigofair/data/ProductTileContainer";
import { ProductTileScreenContainer } from "@faire/web-api--source/indigofair/data/ProductTileScreenContainer";
import { logWarning } from "@faire/web/common/logging";
import React, { PropsWithChildren } from "react";

interface IProductTileImpressionProviderProps {
  loggingData: ComponentLoggingEventParams;
  trackAsBrandClickType?: boolean;
}

// Temporary const until BE adds it to LayoutElement.Type
const TILE_WRAPPER_COMPONENT = "TILE_WRAPPER_COMPONENT";

export const SDUIProductTileImpressionProvider: React.FC<
  PropsWithChildren<IProductTileImpressionProviderProps>
> = ({ children, loggingData, trackAsBrandClickType = false }) => {
  const productTileContainer = getProductTileContainer(loggingData);
  const context = getProductTileImpressionContext(
    productTileContainer,
    loggingData
  );
  const containerName = getProductTileClickImpressionContainerName(loggingData);
  const requestId = loggingData.request_id;

  const screenContext = getProductTileScreenContext(loggingData);
  const containerContext = getProductTileScreenContainerContext(loggingData);
  const contextToken = getContextToken(loggingData);

  const tileType = trackAsBrandClickType
    ? IClickInfo.Type.BRAND
    : IClickInfo.Type.PRODUCT;

  return (
    <TrackedLinkContext.Provider
      value={{
        type: tileType,
        requestId: requestId,
        containerName: containerName ?? "",
      }}
    >
      <ProductTileViewImpressionProvider
        context={context}
        requestId={requestId}
        screenContext={screenContext}
        containerContext={containerContext}
        contextToken={contextToken}
      >
        <ProductTileClickImpressionProvider
          context={context}
          productTileContainer={productTileContainer}
          requestId={requestId}
          containerName={containerName}
          screenContext={screenContext}
          containerContext={containerContext}
          contextToken={contextToken}
        >
          {children}
        </ProductTileClickImpressionProvider>
      </ProductTileViewImpressionProvider>
    </TrackedLinkContext.Provider>
  );
};

const getModifiedViewInstanceIdentifier = (
  loggingData: ComponentLoggingEventParams
): string => {
  if (loggingData.view_display_page === DisplayPageType.SEARCH_PAGE) {
    return loggingData.view_instance_identifier?.split("|")[0] ?? "";
  }
  return loggingData.view_instance_identifier ?? "";
};

export const getContextToken = (loggingData: ComponentLoggingEventParams) => {
  return (
    loggingData.component_instance_identifier ??
    getModifiedViewInstanceIdentifier(loggingData)
  );
};

// eslint-disable-next-line complexity
export const getProductTileImpressionContext = (
  productTileContainer: ProductTileContainer,
  loggingData: ComponentLoggingEventParams
) => {
  const modifiedViewInstanceIdentifier =
    getModifiedViewInstanceIdentifier(loggingData);

  if (
    loggingData.view_display_page === DisplayPageType.CATEGORY_PAGE &&
    (productTileContainer ===
      ProductTileContainer.CATEGORY_PRODUCT_RESULTS_GRID ||
      (loggingData.section_type === "FilterablePaginatedSection" &&
        (loggingData.component_type === ILayoutElement.Type.PRODUCT_TILE_GRID ||
          loggingData.component_type === TILE_WRAPPER_COMPONENT)))
  ) {
    return `${productTileContainer}|${
      loggingData.component_instance_identifier ??
      modifiedViewInstanceIdentifier
    }` as const;
  }

  if (
    productTileContainer ===
      ProductTileContainer.CATEGORY_PRODUCT_RESULTS_LAYOUT_ELEMENT_CAROUSEL &&
    (!loggingData.section_type || !loggingData.component_type)
  ) {
    const identifier =
      loggingData.component_instance_identifier ??
      modifiedViewInstanceIdentifier;
    if (loggingData.component_name) {
      return `${productTileContainer}|${identifier}|${loggingData.component_name}` as const;
    }
    return `${productTileContainer}|${identifier}` as const;
  }

  if (
    loggingData.view_display_page === DisplayPageType.SEARCH_PAGE &&
    loggingData.view_instance_identifier
  ) {
    // view_instance_identifier for the search page is in the format of query|spellCorrectedQuery, but we only want the query
    return `${productTileContainer}|${modifiedViewInstanceIdentifier}` as const;
  }

  if (
    loggingData.view_display_page === DisplayPageType.PRODUCT_PAGE &&
    loggingData.view_instance_identifier
  ) {
    if (
      productTileContainer === ProductTileContainer.PRODUCT_PAGE_LAYOUT_ELEMENT
    ) {
      return `${productTileContainer}|${loggingData.view_instance_identifier}|${loggingData.component_name}` as const;
    }
    return `${productTileContainer}|${loggingData.view_instance_identifier}` as const;
  }

  if (!loggingData.component_name) {
    return `${productTileContainer}|unknown` as const;
  }

  if (
    !loggingData.component_instance_identifier &&
    !loggingData.view_instance_identifier
  ) {
    return `${productTileContainer}|${loggingData.component_name}` as const;
  }

  return `${productTileContainer}|${loggingData.component_name}|${
    loggingData.component_instance_identifier ?? modifiedViewInstanceIdentifier
  }` as const;
};

export const getProductTileScreenContext = (
  loggingData: ComponentLoggingEventParams
): Surface => {
  switch (loggingData.view_display_page) {
    case DisplayPageType.CATEGORY_PAGE:
      return Surface.CATEGORY_PRODUCT_RESULTS;
    case DisplayPageType.HOMEPAGE:
      return Surface.HOME;
    case DisplayPageType.SEARCH_PAGE:
      return Surface.SEARCH_PRODUCT_RESULTS;
    case DisplayPageType.DIGEST_PAGE:
      return Surface.DIGEST_PAGE_SCREEN;
    case DisplayPageType.BRAND_DETAILS_PAGE:
      return Surface.BRAND_PAGE;
    case DisplayPageType.CART_PAGE:
      return Surface.CART;
    case DisplayPageType.PRODUCT_PAGE:
      return Surface.PRODUCT;
    case DisplayPageType.SIMILAR_PRODUCTS_PAGE:
      return Surface.SIMILAR_PRODUCTS;
    case DisplayPageType.SEE_MORE_PAGE:
      return Surface.SEE_MORE_PAGE_SCREEN;
    case DisplayPageType.BROWSE_LANDING_PAGE: // This is for mobile-apps only
    default:
      return Surface.SURFACE_UNKNOWN;
  }
};

export const getProductTileScreenContainerContext = (
  loggingData: ComponentLoggingEventParams
): ProductTileScreenContainer => {
  if (
    loggingData.view_display_page === DisplayPageType.SEARCH_PAGE &&
    loggingData.component_name === SEARCH_IRRELEVANCE_CAROUSEL_COMPONENT_NAME
  ) {
    return ProductTileScreenContainer.SEARCH_PRODUCT_RESULTS_IRRELEVANCE_PRODUCTS;
  }

  return loggingData.component_name as ProductTileScreenContainer;
};

export const getProductTileContainer = (
  loggingData: ComponentLoggingEventParams
): ProductTileContainer => {
  switch (loggingData.view_display_page) {
    case DisplayPageType.CATEGORY_PAGE:
      if (
        loggingData.section_type === "FilterablePaginatedSection" &&
        (loggingData.component_type === ILayoutElement.Type.PRODUCT_TILE_GRID ||
          loggingData.component_type === TILE_WRAPPER_COMPONENT)
      ) {
        return ProductTileContainer.CATEGORY_PRODUCT_RESULTS_GRID;
      }

      return ProductTileContainer.CATEGORY_PRODUCT_RESULTS_LAYOUT_ELEMENT_CAROUSEL;
    case DisplayPageType.HOMEPAGE:
      return ProductTileContainer.HOMEPAGE_LAYOUT_ELEMENT_CAROUSEL;
    case DisplayPageType.SEE_MORE_PAGE:
      return ProductTileContainer.SEE_MORE_PAGE_RESULTS_GRID;
    case DisplayPageType.DIGEST_PAGE:
      return ProductTileContainer.DIGEST_PAGE_LAYOUT_ELEMENT;
    case DisplayPageType.BRAND_DETAILS_PAGE:
      return ProductTileContainer.BRAND_PAGE_LAYOUT_ELEMENT;
    case DisplayPageType.SEARCH_PAGE:
      if (
        loggingData.component_name ===
          SEARCH_IRRELEVANCE_CAROUSEL_COMPONENT_NAME ||
        loggingData.component_name ===
          ProductTileScreenContainer.SEARCH_PRODUCT_RESULTS_IRRELEVANCE_PRODUCTS
      ) {
        return ProductTileContainer.SEARCH_PRODUCT_RESULTS_IRRELEVANCE_CAROUSEL;
      }
      if (
        loggingData.component_name ===
        ProductTileScreenContainer.SEARCH_PRODUCT_RESULTS_BI_PRODUCTS
      ) {
        return ProductTileContainer.SEARCH_PRODUCT_RESULTS_BI_GRID;
      }
      if (
        loggingData.component_name ===
        ProductTileScreenContainer.SEARCH_PRODUCT_RESULTS_SUGGESTED_BRAND_PRODUCTS
      ) {
        return ProductTileContainer.SEARCH_PRODUCT_RESULTS_SUGGESTED_BRAND;
      }
      if (
        loggingData.section_type === "FilterablePaginatedSection" &&
        (loggingData.component_type === ILayoutElement.Type.PRODUCT_TILE_GRID ||
          loggingData.component_type === TILE_WRAPPER_COMPONENT)
      ) {
        return ProductTileContainer.SEARCH_PRODUCT_RESULTS_GRID;
      }
      return ProductTileContainer.SEARCH_PRODUCT_RESULTS_LAYOUT_ELEMENT;
    case DisplayPageType.PRODUCT_PAGE:
      if (loggingData.component_type?.endsWith("_GRID")) {
        return ProductTileContainer.PRODUCT_PAGE_PRODUCT_GRID;
      }
      if (loggingData.component_type?.endsWith("_CAROUSEL")) {
        return ProductTileContainer.PRODUCT_PAGE_LAYOUT_ELEMENT;
      }
      return ProductTileContainer.PRODUCT_TILE_CONTAINER_UNKNOWN;
    default:
      logWarning(
        new Error(
          `SDUI ProductTileContainer is UNKNOWN for ${loggingData?.view_display_page}`
        ),
        {
          data: loggingData,
        }
      );
      return ProductTileContainer.PRODUCT_TILE_CONTAINER_UNKNOWN;
  }
};

/**
 * This is needed to match legacy /click-track API container names
 */
export const getProductTileClickImpressionContainerName = (
  loggingData: ComponentLoggingEventParams
): string | undefined => {
  if (
    loggingData.view_display_page === DisplayPageType.CATEGORY_PAGE &&
    loggingData.section_type === "FilterablePaginatedSection" &&
    (loggingData.component_type === ILayoutElement.Type.PRODUCT_TILE_GRID ||
      loggingData.component_type === TILE_WRAPPER_COMPONENT) &&
    loggingData.component_name === "CATEGORY_PRODUCT_RESULTS_PRODUCTS"
  ) {
    return `${CATEGORY_PRODUCT_TRACK_CLICK_CONTAINER_NAME}_old`;
  }

  return loggingData.component_name;
};
